/*
 * "UNKNOWN · ADD": FILLING IN A FACT FROM THE BREAKDOWN (frame 1c, "Founded ·
 * unknown · Add").
 *
 * The score is live, never stored: GET /companies/:id runs the real scorer over
 * the stored facts every time (src/automations/cardScore.js). So an Add is only
 * ever a write of the fact itself, to the place the scorer reads it from, and the
 * shell's reload brings the new score back. This module says, per group, what the
 * editor asks for and which write it becomes:
 *
 *   industry    companies.industry, picked from the values the column already
 *               holds (COMPUTER_SOFTWARE, never a typed "B2B SaaS": a coined value
 *               would match nothing on the board's filters, and the column is an
 *               enum in all but name)
 *   headcount   companies.employee_range, a whole number as text (leadFacts reads
 *               Number() of it)
 *   title       the SCORED contact's job_title: on a colleague's record the score
 *               read the point of contact's title, so that is the one to fill in
 *   funding     properties_set { total_funding } (PR #154's allowlist; the server
 *               reads "38M" and "$38,000,000" the same)
 *   age         properties_set { founded_year }
 *   country     companies.country (the server snaps "usa" to "United States")
 *
 * The client checks the shape first so a typo answers beside the field at once;
 * the server checks again and its 400 is shown the same way.
 *
 * Pure, so test/record-score-add.test.js pins it without a browser.
 */
import { enumWords } from "./format.mjs";

export const ADD_EDITORS = {
  industry: { input: "select", label: "Industry", placeholder: "Pick an industry" },
  headcount: { input: "number", label: "Employees", placeholder: "180" },
  title: { input: "text", label: "Title", placeholder: "VP Marketing" },
  funding: { input: "text", label: "Total funding", placeholder: "38M" },
  age: { input: "number", label: "Founded year", placeholder: "2015" },
  country: { input: "text", label: "Country", placeholder: "United States" },
};

/** The editor for a breakdown row's Add, or null when the row has none. */
export function addEditor(row) {
  if (!row || !row.add || !row.unknown) return null;
  return ADD_EDITORS[row.group] || null;
}

// The server's money reading (src/db/recordFields.js readMoney), so the client
// refuses exactly what the server would.
const MONEY = /^(\d+(?:\.\d+)?)([kmb])?$/;

/**
 * The write an Add becomes:
 *   { kind: "company", body }               PATCH /companies/:id
 *   { kind: "contact", id, body }           PATCH /contacts/:id
 *   { kind: "props", id, props }            PATCH /companies/:id { properties_set }
 *   { error }                               shown beside the field, nothing sent
 */
export function addRequest(row, value, { companyId = null, contactId = null, scoredContactId = null, now = new Date() } = {}) {
  const editor = addEditor(row);
  if (!editor) return { error: "This fact can't be added from here." };
  const text = value === null || value === undefined ? "" : String(value).trim();
  if (!text) return { error: `Type the ${editor.label.toLowerCase()} first.` };

  switch (row.group) {
    case "industry":
      if (!companyId) return { error: "There is no company to add it to." };
      return { kind: "company", body: { industry: text } };
    case "headcount": {
      const n = Number(text.replace(/[,\s]/g, ""));
      if (!Number.isInteger(n) || n <= 0) return { error: "Employees has to be a whole number, like 180." };
      if (!companyId) return { error: "There is no company to add it to." };
      return { kind: "company", body: { employee_range: String(n) } };
    }
    case "title": {
      const id = scoredContactId || contactId;
      if (!id) return { error: "There is no one to add it to." };
      if (text.length > 300) return { error: "Title is longer than 300 characters." };
      return { kind: "contact", id, body: { job_title: text } };
    }
    case "funding": {
      const raw = text.toLowerCase().replace(/[$,\s]/g, "");
      if (!MONEY.test(raw)) return { error: "Funding has to be an amount, like 38M or 38000000." };
      if (!companyId) return { error: "There is no company to add it to." };
      return { kind: "props", id: companyId, props: { total_funding: text } };
    }
    case "age": {
      const year = Number(text);
      if (!Number.isInteger(year) || year < 1800 || year > new Date(now).getFullYear() + 1) {
        return { error: "Founded has to be a year, like 2015." };
      }
      if (!companyId) return { error: "There is no company to add it to." };
      return { kind: "props", id: companyId, props: { founded_year: year } };
    }
    case "country":
      if (!companyId) return { error: "There is no company to add it to." };
      return { kind: "company", body: { country: text } };
    default:
      return { error: "This fact can't be added from here." };
  }
}

/**
 * The industry picker's options: the values the column already holds (GET
 * /contacts?stats=1 facets.industry), in words, sorted, each once.
 */
export function industryOptions(facets) {
  const values = [...new Set(((facets && facets.industry) || []).filter((v) => typeof v === "string" && v.trim()))];
  return values
    .map((value) => ({ value, label: enumWords(value) }))
    .sort((a, b) => a.label.localeCompare(b.label));
}
